import type { FileItem } from '@erp/shared';
import { Paperclip, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { formatSize } from '@/lib/format';

/** Прикрепление файлов к форме: файлы сразу загружаются на сервер, в форму уходят их id. */
export function FileUpload({
  value,
  onChange,
  id,
  invalid,
  label = 'Прикрепить файл',
}: {
  value: FileItem[];
  onChange: (files: FileItem[]) => void;
  id?: string;
  invalid?: boolean;
  label?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(list: FileList | null) {
    if (!list?.length) return;
    setUploading(true);
    setError(null);
    const added: FileItem[] = [];
    try {
      for (const file of Array.from(list)) {
        const fd = new FormData();
        fd.append('file', file);
        added.push(await api<FileItem>('/files', { method: 'POST', body: fd }));
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      onChange([...value, ...added]);
      setUploading(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div>
      {value.length > 0 && (
        <ul className="mb-2 flex flex-col gap-1">
          {value.map((f) => (
            <li key={f.id} className="flex items-center gap-2 rounded-md bg-paper px-2.5 py-1.5 text-[13px]">
              <Paperclip className="size-3.5 shrink-0 text-steel" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <span className="shrink-0 text-steel">{formatSize(f.size)}</span>
              <button
                type="button"
                aria-label={`Убрать ${f.name}`}
                onClick={() => onChange(value.filter((x) => x.id !== f.id))}
                className="text-steel hover:text-danger"
              >
                <X className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        ref={input}
        id={id}
        type="file"
        multiple
        className="sr-only"
        onChange={(e) => upload(e.target.files)}
      />
      <button
        type="button"
        aria-invalid={invalid}
        onClick={() => input.current?.click()}
        disabled={uploading}
        className="inline-flex h-9 items-center gap-2 rounded-[var(--radius-control)] border border-dashed border-line-strong px-3 text-[13.5px] text-ink-soft hover:bg-sunken aria-[invalid=true]:border-danger"
      >
        <Paperclip className="size-4" aria-hidden />
        {uploading ? 'Загрузка…' : label}
      </button>
      {error && <p className="mt-1 text-[12.5px] text-danger">{error}</p>}
    </div>
  );
}

export function FileList({ files }: { files: FileItem[] }) {
  if (!files.length) return null;
  return (
    <ul className="flex flex-col gap-1">
      {files.map((f) => (
        <li key={f.id}>
          <a
            href={f.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex max-w-full items-center gap-1.5 text-[13.5px] text-link hover:underline"
          >
            <Paperclip className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">{f.name}</span>
            <span className="shrink-0 text-steel">{formatSize(f.size)}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}
